import React, { useState } from "react";
import { ICommissionGroup } from "../../models/commissionAndRepayment.model";
import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  XCircle,
} from "lucide-react";
import { formatDateTime } from "../../utils/formatDateUtils";
import {
  CommissionPDFData,
  generateCommissionPDF,
} from "../../pages/EPCCustomerManager/generateCommissionPDF";
import Button, { ButtonType } from "../Assets/Button";

interface ViewCommissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  commission: any;
  onFilterChange?: (startDate: string, endDate: string) => void;
}

export const ViewCommissionModal: React.FC<ViewCommissionModalProps> = ({
  isOpen,
  onClose,
  commission,
  onFilterChange,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const groups: ICommissionGroup[] = commission?.groups ?? [];

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const generateGroupPDF = (group: ICommissionGroup) => {
    if (!group?.commissionHistory?.length) return;

    const pdfData: CommissionPDFData[] = group.commissionHistory.map(
      (item: any) => ({
        commissionId: item._id,
        commissionAmount: item.commissionAmount,
        calculatedCommission: item.calculatedCommission,
        status: item.status,
        plantSize: item.quoteId?.plantSize,
        customerName: item.customerId?.customerName,
        confirmAt: item.quoteId?.confirmAt,
        excessAmount: item.quoteId?.excessAmount,
        customCommission: item.quoteId?.customCommission,
        commissionValue: group.salesRepresentative?.commissionValue,
        adjustmentAmount: item.quoteId?.adjustmentAmount,
        materialDeduction: item.materialDeduction,
      }),
    );

    const approvedBy = group.commissionHistory[0]?.cseId?.username;

    const salesRepName = group.salesRepresentative?.name;
    const panNumber = group.salesRepresentative?.panNumber;
    const bankDetails = {
      bankAccountName: group.salesRepresentative?.bankAccountName,
      bankAccountNumber: group.salesRepresentative?.bankAccountNumber,
      ifsc: group.salesRepresentative?.ifsc,
    };

    generateCommissionPDF(
      pdfData,
      approvedBy,
      salesRepName,
      panNumber,
      bankDetails,
    );
  };

  const handleApplyFilter = () => {
    if (startDate && endDate && onFilterChange) {
      onFilterChange(startDate, endDate);
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    if (onFilterChange) {
      onFilterChange("", "");
    }
  };

  const handleOpenProof = (proof?: string) => {
    if (!proof) return;
    window.open(proof, "_blank", "noopener,noreferrer");
  };

  const calculateBatchTotal = (historyItems: any[]) => {
    return historyItems.reduce((sum, item) => {
      return sum + (item.calculatedCommission || item.commissionAmount || 0);
    }, 0);
  };

  const getStatusBadge = (accountStatus: string) => {
    const baseClasses =
      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";

    if (accountStatus === "approved") {
      return (
        <span
          className={`${baseClasses} bg-green-100 text-green-800 border border-green-200`}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Account Approved
        </span>
      );
    } else if (accountStatus === "rejected") {
      return (
        <span
          className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}
        >
          <XCircle className="w-3 h-3 mr-1" />
          Account Rejected
        </span>
      );
    } else {
      return (
        <span
          className={`${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-200`}
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Pending Account Approval
        </span>
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300">
      <div className="bg-gradient-to-br from-white to-gray-50 w-[800px] h-[70vh] rounded-2xl shadow-2xl shadow-black/10 overflow-hidden border border-gray-200 animate-in fade-in-0 zoom-in-95 duration-300 flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Commission Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200 hover:rotate-90 transform"
          >
            <span className="text-lg">✕</span>
          </button>
        </div>

        {/* Date Filter Panel */}
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type={ButtonType.Submit}
                onClick={handleApplyFilter}
                disabled={!startDate || !endDate}
                className="text-xs"
              >
                Apply
              </Button>
              <Button
                type={ButtonType.Cancel}
                onClick={handleClearFilter}
                className="text-xs"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No Commission Records
              </h3>
              <p className="text-sm text-gray-500 max-w-md">
                No commission history found for this sales representative.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group: ICommissionGroup, index: number) => {
                const groupKey = `${group.cseApprovedAt}-${index}`;
                const isExpanded = expandedGroups.has(groupKey);
                const batchTotal = calculateBatchTotal(group.commissionHistory);

                return (
                  <div
                    key={groupKey}
                    className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-all duration-300"
                  >
                    <div
                      className="px-5 py-4 bg-gradient-to-r from-gray-50 to-blue-50 hover:from-blue-50 hover:to-blue-100 cursor-pointer transition-colors duration-200"
                      onClick={() => toggleGroup(groupKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          )}
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                            <span className="font-medium text-gray-900">
                              {formatDateTime(group.cseApprovedAt || "")}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600">
                            ({group.commissionHistory.length} Customers)
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <div className="text-sm text-gray-500 mr-2">
                              Batch Total:
                            </div>
                            <div className="text-sm text-emerald-600 font-bold">
                              ₹{batchTotal.toLocaleString("en-IN")}
                            </div>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              generateGroupPDF(group);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md
               bg-sky-600 hover:bg-sky-700 text-white transition-all shadow-sm"
                          >
                            <FileText className="w-4 h-4" />
                            PDF
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="px-5 py-4 border-t border-gray-100 space-y-3">
                        {group.commissionHistory.map((h: any, idx: number) => {
                          const customerName =
                            h.customerId?.customerName || "-";

                          const amount =
                            h.calculatedCommission + (h.adjustAmount || 0) ||
                            h.commissionAmount ||
                            0;

                          return (
                            <div
                              key={h._id || idx}
                              className="flex items-center justify-between rounded-md p-3 border border-slate-100 bg-white shadow-sm"
                            >
                              {/* LEFT SIDE */}
                              <div className="flex items-start gap-3">
                                <div className="text-sm">
                                  <div className="font-medium text-slate-800">
                                    {customerName}
                                    <span className="text-slate-600">
                                      {" "}
                                      ({h.quoteId?.plantSize} kW)
                                    </span>
                                  </div>

                                  {/* ===== NORMAL COMMISSION ===== */}
                                  {!h.quoteId?.customCommissionType &&
                                    !h.quoteId?.excessAmount && (
                                      <div className="flex justify-between gap-8 text-xs text-slate-400 mt-1">
                                        <span>
                                          Commission (
                                          {
                                            group.salesRepresentative
                                              ?.commissionType
                                          }
                                          )
                                        </span>{" "}
                                        <span>₹ {h.commissionAmount}</span>
                                      </div>
                                    )}

                                  {/* ===== CUSTOM COMMISSION ===== */}
                                  {h.quoteId?.customCommissionType && (
                                    <div className="space-y-1 text-xs text-slate-400 mt-1">
                                      <div className="flex justify-between gap-8 text-xs text-slate-400 mt-1">
                                        <span>
                                          Commission (
                                          {
                                            group.salesRepresentative
                                              ?.commissionType
                                          }
                                          )
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          ₹{" "}
                                          {
                                            group.salesRepresentative
                                              ?.commissionValue
                                          }
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>
                                          Total
                                          {group.salesRepresentative
                                            ?.commissionType === "per_kw" && (
                                            <>
                                              {" "}
                                              (
                                              {
                                                group.salesRepresentative
                                                  ?.commissionValue
                                              }{" "}
                                              × {h.quoteId?.plantSize} kW)
                                            </>
                                          )}
                                        </span>
                                        <span className="font-medium text-slate-700">
                                          ₹ {h.commissionAmount}
                                        </span>
                                      </div>

                                      <div className="flex justify-between">
                                        <span>Deduction</span>
                                        <span className="font-medium text-red-600">
                                          − ₹ {h.quoteId.customCommission}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {/* ===== EXCESS AMOUNT ===== */}
                                  {h.quoteId?.excessAmount && (
                                    <div className="space-y-1 text-xs text-slate-400 mt-1">
                                      <div className="flex justify-between">
                                        <span>
                                          Total
                                          {commission.salesRepresentative
                                            ?.commissionType === "per_kw" && (
                                            <>
                                              {" "}
                                              (
                                              {
                                                commission.salesRepresentative
                                                  ?.commissionValue
                                              }{" "}
                                              × {h.quoteId?.plantSize} kW)
                                            </>
                                          )}
                                        </span>
                                        <span className="font-medium text-slate-700">
                                          ₹{h.commissionAmount}
                                        </span>
                                      </div>

                                      <div className="flex justify-between">
                                        <span>Excess Amount</span>
                                        <span className="font-medium text-green-600">
                                          + ₹{h.quoteId.excessAmount}
                                        </span>
                                      </div>

                                      <p className="text-xs text-red-600">
                                        Amount shown is post 8.9% GST deduction
                                      </p>
                                    </div>
                                  )}
                                  {/* ===== Adjustment AMOUNT ===== */}

                                  {h.quoteId?.adjustmentAmount && (
                                    <div className="flex justify-between text-xs text-red-600 item-center mt-1">
                                      <span>Adjustment Amount:</span>
                                      <span className="font-medium">
                                        ₹ {h.quoteId?.adjustmentAmount}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* RIGHT SIDE */}
                              <div className="text-sm text-right">
                                <div className="flex items-center justify-end gap-2 font-semibold text-lg">
                                  {group.salesRepresentative?.commissionType ===
                                    "per_kw" &&
                                    !h.quoteId?.customCommissionType &&
                                    !h.quoteId?.excessAmount && (
                                      <span className="text-xs text-gray-400">
                                        ({h.commissionAmount} ×{" "}
                                        {h.quoteId.plantSize} kW)
                                      </span>
                                    )}

                                  <span>₹{amount.toLocaleString("en-IN")}</span>
                                </div>

                                <div className="space-y-1.5 flex flex-col items-end mt-2">
                                  {h.status && (
                                    <div className="flex items-center text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                                      <CheckCircle className="w-3 h-3 text-emerald-800 mr-1" />
                                      {h.status.capitalizeWords()} by{" "}
                                      {h.cseId.username}
                                    </div>
                                  )}

                                  {h.accountStatus &&
                                    getStatusBadge(h.accountStatus)}

                                  {h.proof && (
                                    <button
                                      onClick={() => handleOpenProof(h.proof)}
                                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      <Download className="w-3 h-3" />
                                      View Proof
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="space-x-2">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              className="text-xs"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
